import React, { useState, useEffect } from "react";
import Header from "./Header";
import Sidebar from "./Sidebar";
import SubHeader from "./SubHeader";
import '../Style/AddEmployee.css';
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { User, Briefcase, Mail, Phone, MapPin, Building2, Image as ImageIcon, X, Save, ArrowLeft, ChevronDown, Home  } from 'lucide-react';
import { BASE_URL } from "../Apis/apiConfig";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DatePicker from "react-datepicker";

const AddEmployee = () => {
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const location = useLocation();

  const initialCompanyName = location.state?.companyName || "";

  const [formData, setFormData] = useState({
    employeeName: "",
    designation: "",
    email: "",
    phone: "",
    address: "",
    companyName: initialCompanyName,
    gateNumber: ""
  });

  const [errors, setErrors] = useState({});
  const [providers, setProviders] = useState([]);
  const societyName = localStorage.getItem("society");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageError, setImageError] = useState("");
  const [joiningDate, setJoiningDate] = useState(null);
  const [resignDate, setResignDate] = useState(null);

  const MAX_IMAGE_SIZE = 5 * 1024 * 1024;

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  useEffect(() => {
    const fetchProviders = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${BASE_URL}/api/services/get-all-services?societyName=${encodeURIComponent(societyName)}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setProviders(response.data);
      } catch (error) {
        console.error("Failed to fetch providers", error);
        toast.error("Failed to fetch providers");
      }
    };

    if (!initialCompanyName) {
      fetchProviders();
    }
  }, [societyName, initialCompanyName]);

  const validate = () => {
    const newErrors = {};
    if (!formData.companyName) newErrors.companyName = "Company is required";
    if (!formData.employeeName) newErrors.employeeName = "Employee name is required";
    if (!formData.designation) newErrors.designation = "Designation is required";
    if (!formData.email) newErrors.email = "Email is required";
    if (!formData.phone) newErrors.phone = "Phone is required";
    if (!formData.address) newErrors.address = "Address is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: "" });
    }
  };

  const formatDate = (date) => {
  if (!date) return "";
  return new Date(date).toISOString().split("T")[0]; // YYYY-MM-DD
};

  const handleFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    const allowed = ["image/jpeg", "image/png", "image/webp", "image/gif"];
    if (!allowed.includes(file.type)) {
      setImageError("Only JPG, PNG, WEBP or GIF allowed.");
      setImageFile(null);
      setImagePreview(null);
      return;
    }
    if (file.size > MAX_IMAGE_SIZE) {
      setImageError("File too large (max 5MB).");
      setImageFile(null);
      setImagePreview(null);
      return;
    }

    setImageError("");
    setImageFile(file);

    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setImageError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const payload = new FormData();
    payload.append("employeeName", formData.employeeName);
    payload.append("designation", formData.designation);
    payload.append("email", formData.email);
    payload.append("phone", formData.phone);
    payload.append("address", formData.address);
    payload.append("companyName", formData.companyName);
    payload.append("societyName", societyName || "");
    payload.append("joiningDate", formatDate(joiningDate));
    payload.append("resignDate", formatDate(resignDate));

    if (imageFile) {
      payload.append("employeeImage", imageFile);
    }

    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${BASE_URL}/api/employee/add-employee`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      toast.success("Employee added successfully.");
      navigate("/service-provider");
    } catch (error) {
      console.error("Error adding employee:", error);
      toast.error("Failed to add employee");
    }
  };

  

  return (
    <div className="app-container">
      <Header toggleSidebar={toggleSidebar} />
      <ToastContainer position="bottom-right" autoClose={3000} />

      <div className="main-layout">
        <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

        <main className="content-area">
          <SubHeader />
          <div className="premium-form-container">
            <div className="premium-card">
              <div className="card-header-premium">
                <div className="header-content">
                  <button className="btn-back-icon-premium" onClick={() => navigate(-1)}>
                    <ArrowLeft size={20} />
                  </button>
                  <div>
                    <h2 className="form-title">Add New Employee</h2>
                    <p className="form-subtitle">Register a new employee for a service provider</p>
                  </div>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="premium-form-body">
                <div className="form-grid-premium">
                  <div className="form-group-premium full-width">
                    <label className="label-premium">
                      <Building2 size={16} />
                      <span>Service Provider</span>
                      <span className="required">*</span>
                    </label>
                    <div className="input-wrapper-premium">
                      {initialCompanyName ? (
                        <input
                          type="text"
                          className="input-premium readonly"
                          value={initialCompanyName}
                          readOnly
                        />
                      ) : (
                        <div className="select-wrapper">
                          <select
                            name="companyName"
                            value={formData.companyName}
                            onChange={handleChange}
                            className={`input-premium ${errors.companyName ? 'error' : ''}`}
                          >
                            <option value="">Select a Service Provider</option>
                            {providers.map((p) => (
                              <option key={p._id} value={p.companyName}>
                                {p.companyName}
                              </option>
                            ))}
                          </select>
                          <ChevronDown size={16} className="select-icon" />
                        </div>
                      )}
                    </div>
                    {errors.companyName && <span className="error-text">{errors.companyName}</span>}
                  </div>

                  <div className="form-group-premium">
                    <label className="label-premium">
                      <User size={16} />
                      <span>Employee Name</span>
                      <span className="required">*</span>
                    </label>
                    <div className="input-wrapper-premium">
                      <input
                        type="text"
                        name="employeeName"
                        value={formData.employeeName}
                        onChange={handleChange}
                        className={`input-premium ${errors.employeeName ? 'error' : ''}`}
                        placeholder="e.g. John Doe"
                      />
                    </div>
                    {errors.employeeName && <span className="error-text">{errors.employeeName}</span>}
                  </div>

                 <div className="form-group-premium">
                  <label className="label-premium">
                    <Briefcase size={16} />
                    <span>Designation</span>
                    <span className="required">*</span>
                  </label>
                  <div className="input-wrapper-premium">
                    <input
                      type="text"
                      name="designation"
                      value={formData.designation}
                      onChange={handleChange}
                      className={`input-premium ${errors.designation ? 'error' : ''}`}
                      placeholder="e.g. Security Guard"
                    />
                  </div>
                  {errors.designation && <span className="error-text">{errors.designation}</span>}
                </div>


                {formData.designation.toLowerCase() === 'security guard' && (
                  <div className="form-group-premium">
                    <label className="label-premium">
                      <Home size={16} />
                      <span>GATE NUMBER</span>
                      <span className="required">*</span>
                    </label>
                    <div className="input-wrapper-premium">
                      <input
                        type="text"
                        name="gateNumber"
                        value={formData.gateNumber}
                        onChange={handleChange}
                        className={`input-premium ${errors.gateNumber ? 'error' : ''}`}
                        placeholder="Enter gate number"
                      />
                    </div>
                    {errors.gateNumber && <span className="error-text">{errors.gateNumber}</span>}
                  </div>
                  )}
                  <div className="form-group-premium">
                    <label className="label-premium">
                      <Mail size={16} />
                      <span>Email Address</span>
                      <span className="required">*</span>
                    </label>
                    <div className="input-wrapper-premium">
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className={`input-premium ${errors.email ? 'error' : ''}`}
                        placeholder="john@example.com"
                      />
                    </div>
                    {errors.email && <span className="error-text">{errors.email}</span>}
                  </div>

                  <div className="form-group-premium">
                    <label className="label-premium">
                      <Phone size={16} />
                      <span>Phone Number</span>
                      <span className="required">*</span>
                    </label>
                    <div className="input-wrapper-premium">
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className={`input-premium ${errors.phone ? 'error' : ''}`}
                        placeholder="+91 98765 43210"
                      />
                    </div>
                    {errors.phone && <span className="error-text">{errors.phone}</span>}
                  </div>

                  <div className="form-group-premium full-width">
                    <label className="label-premium">
                      <MapPin size={16} />
                      <span>Full Address</span>
                      <span className="required">*</span>
                    </label>
                    <div className="input-wrapper-premium">
                      <input
                        type="text"
                        name="address"
                        value={formData.address}
                        onChange={handleChange}
                        className={`input-premium ${errors.address ? 'error' : ''}`}
                        placeholder="Enter complete residential address"
                      />
                    </div>
                    {errors.address && <span className="error-text">{errors.address}</span>}
                  </div>
                     <div className="form-group-premium full-width">
  <div style={{ display: "flex", gap: "20px" }}>

    {/* JOINING DATE */}
    <div style={{ flex: 1 }}>
      <label className="label-premium">
        <span>Joining Date</span>
      </label>

      <div className="input-wrapper-premium">
        <DatePicker
          selected={joiningDate}
          onChange={(date) => setJoiningDate(date)}
          dateFormat="dd/MM/yyyy"
          placeholderText="Select Joining Date"
          className="input-premium"
        />
      </div>
    </div>

    {/* RESIGN DATE */}
    <div style={{ flex: 1 }}>
      <label className="label-premium">
        <span>Resign Date</span>
      </label>

      <div className="input-wrapper-premium">
        <DatePicker
          selected={resignDate}
          onChange={(date) => setResignDate(date)}
          dateFormat="dd/MM/yyyy"
          placeholderText="Select Resign Date"
          className="input-premium"
          minDate={joiningDate}
        />
      </div>
    </div>

  </div>
</div>

                  <div className="form-group-premium full-width">
                    <label className="label-premium">
                      <ImageIcon size={16} />
                      <span>Profile Photo</span>
                    </label>
                    <div className={`file-upload-premium ${imageError ? 'error' : ''}`}>
                      <input
                        type="file"
                        id="employee-image"
                        accept="image/*"
                        onChange={handleFileChange}
                        className="hidden-file-input"
                      />

                      {!imagePreview ? (
                        <label htmlFor="employee-image" className="upload-placeholder">
                          <div className="upload-icon-circle">
                            <ImageIcon size={24} />
                          </div>
                          <span>Click to upload photo</span>
                          <span className="upload-hint">JPG, PNG, WEBP (Max 5MB)</span>
                        </label>
                      ) : (
                        <div className="image-preview-area">
                          <img src={imagePreview} alt="Preview" className="preview-img" />
                          <div className="preview-info">
                            <span className="file-name">{imageFile?.name}</span>
                            <button type="button" onClick={removeImage} className="btn-remove-image">
                              <X size={14} /> Remove
                            </button>
                          </div>
                       
                        </div>
                      )}
                    </div>
                    {imageError && <span className="error-text">{imageError}</span>}
                  </div>

                </div>

                <div className="form-actions-premium">
                  <button type="button" className="btn-secondary-premium" onClick={() => navigate(-1)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary-premium">
                    <Save size={18} />
                    <span>Save Employee</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default AddEmployee;
